// src/pages/LabelDesignPage.js
// Visual label designer — drag-and-drop canvas with absolute-positioned blocks.
// Each block has x, y, w, h in inches. Saved as { canvas: [...] } in the rows JSON column.
// Backward compat: old columns/rows designs are auto-converted to canvas on load.
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import productLabelService from '../services/productLabelService';
import LabelRenderer from '../components/LabelRenderer';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 9);
const snap = (v, g = 0.05) => Math.round(v / g) * g;
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// ─── Constants ────────────────────────────────────────────────────────────────

const BLOCK_TYPES = [
  { value: 'product_name',         label: 'Product Name' },
  { value: 'net_weight',           label: 'Net Weight' },
  { value: 'po_number',            label: 'PO Number' },
  { value: 'plu_sku',              label: 'PLU / SKU' },
  { value: 'brand_logo',           label: 'Brand Logo' },
  { value: 'storage_banner',       label: 'Storage Banner' },
  { value: 'cooking_instructions', label: 'Cooking Instructions' },
  { value: 'ingredients',          label: 'Ingredients' },
  { value: 'allergen_statement',   label: 'Allergen Statement' },
  { value: 'nutrition_facts',      label: 'Nutrition Facts' },
  { value: 'upc_barcode',          label: 'UPC Barcode' },
  { value: 'upc_text',             label: 'UPC Text' },
  { value: 'gs1_128_barcode',      label: 'GS1-128 Case Barcode' },
  { value: 'lot_exp_box',          label: 'Lot / Exp Box' },
  { value: 'qr_code',              label: 'QR Code' },
  { value: 'manufacturer_info',    label: 'Manufacturer Info' },
  { value: 'country_of_origin',    label: 'Country of Origin' },
  { value: 'custom_text',          label: 'Custom Text' },
  { value: 'custom_image',         label: 'Custom Image' },
  { value: 'usda_inspection_mark', label: 'USDA Inspection Mark' },
  { value: 'divider',              label: 'Divider' },
];

const BLOCK_COLORS = {
  product_name:         '#2563eb',
  net_weight:           '#7c3aed',
  po_number:            '#7c2d12',
  plu_sku:              '#9a3412',
  brand_logo:           '#0891b2',
  storage_banner:       '#dc2626',
  cooking_instructions: '#0d9488',
  ingredients:          '#16a34a',
  allergen_statement:   '#d97706',
  nutrition_facts:      '#9333ea',
  upc_barcode:          '#475569',
  upc_text:             '#64748b',
  gs1_128_barcode:      '#334155',
  lot_exp_box:          '#0f766e',
  qr_code:              '#2563eb',
  manufacturer_info:    '#92400e',
  country_of_origin:    '#6b7280',
  custom_text:          '#b45309',
  custom_image:         '#be185d',
  usda_inspection_mark: '#b91c1c',
  divider:              '#94a3b8',
};

// Default width × height in inches when adding a block
const BLOCK_DEFAULTS = {
  product_name:         { w: 3.0, h: 0.50 },
  net_weight:           { w: 1.5, h: 0.30 },
  po_number:            { w: 1.5, h: 0.30 },
  plu_sku:              { w: 1.5, h: 0.30 },
  brand_logo:           { w: 1.5, h: 1.00 },
  storage_banner:       { w: 3.0, h: 0.35 },
  cooking_instructions: { w: 3.0, h: 1.00 },
  ingredients:          { w: 3.0, h: 1.50 },
  allergen_statement:   { w: 3.0, h: 0.35 },
  nutrition_facts:      { w: 2.5, h: 4.00 },
  upc_barcode:          { w: 1.5, h: 0.80 },
  upc_text:             { w: 1.5, h: 0.25 },
  gs1_128_barcode:      { w: 2.5, h: 0.65 },
  lot_exp_box:          { w: 2.0, h: 0.50 },
  qr_code:              { w: 1.0, h: 1.00 },
  manufacturer_info:    { w: 3.0, h: 0.60 },
  country_of_origin:    { w: 2.0, h: 0.25 },
  custom_text:          { w: 2.0, h: 0.35 },
  custom_image:         { w: 1.5, h: 1.00 },
  usda_inspection_mark: { w: 1.2, h: 1.2 },
  divider:              { w: 4.0, h: 0.05 },
};

const LABEL_PRESETS = [
  { label: '4×6 Landscape (default)', width: 6,    height: 4   },
  { label: '4×6 Portrait',            width: 4,    height: 6   },
  { label: '4×3',                     width: 3,    height: 4   },
  { label: '3×2',                     width: 2,    height: 3   },
  { label: '2×4',                     width: 4,    height: 2   },
  { label: '8.5×11 Landscape',        width: 11,   height: 8.5 },
  { label: 'Custom',                  width: null, height: null },
];

// ─── Convert old columns/rows layout → flat canvas blocks ─────────────────────

function convertToCanvas(layout, W, H) {
  const blocks = [];

  if (layout.columns && layout.columns.length > 0) {
    const panels = layout.columns;
    const totalFlex = panels.reduce((s, p) => s + (p.flex || 1), 0);
    let panelX = 0;

    for (const panel of panels) {
      const panelW = ((panel.flex || 1) / totalFlex) * W;

      if (panel.rows && panel.rows.length > 0) {
        const totalRowFlex = panel.rows.reduce((s, r) => s + (r.flex || 1), 0);
        let rowY = 0;
        for (const row of panel.rows) {
          const rowH = ((row.flex || 1) / totalRowFlex) * H;
          const cols = row.columns || [];
          const totalColFlex = cols.reduce((s, c) => s + (c.flex || 1), 0) || 1;
          let colX = panelX;
          for (const col of cols) {
            const colW = ((col.flex || 1) / totalColFlex) * panelW;
            const colBlocks = col.blocks || [];
            const blockH = colBlocks.length > 0 ? rowH / colBlocks.length : rowH;
            const cx = colX; const ry = rowY; // capture before forEach
            colBlocks.forEach((b, bi) => {
              blocks.push({ ...b, id: uid(), x: snap(cx), y: snap(ry + bi * blockH), w: snap(colW), h: snap(blockH) });
            });
            colX += colW;
          }
          rowY += rowH;
        }
      } else if (panel.blocks && panel.blocks.length > 0) {
        const pb = panel.blocks;
        const blockH = H / pb.length;
        const px = panelX; // capture before forEach
        pb.forEach((b, bi) => {
          blocks.push({ ...b, id: uid(), x: snap(px), y: snap(bi * blockH), w: snap(panelW), h: snap(blockH) });
        });
      }
      panelX += panelW;
    }
  } else if (layout.rows && layout.rows.length > 0) {
    const rows = layout.rows;
    const totalRowFlex = rows.reduce((s, r) => s + (r.flex || 1), 0);
    let rowY = 0;
    for (const row of rows) {
      const rowH = ((row.flex || 1) / totalRowFlex) * H;
      const cols = row.columns || [];
      const totalColFlex = cols.reduce((s, c) => s + (c.flex || 1), 0) || 1;
      let colX = 0;
      for (const col of cols) {
        const colW = ((col.flex || 1) / totalColFlex) * W;
        const colBlocks = col.blocks || [];
        const blockH = colBlocks.length > 0 ? rowH / colBlocks.length : rowH;
        const cx = colX; const ry = rowY; // capture before forEach
        colBlocks.forEach((b, bi) => {
          blocks.push({ ...b, id: uid(), x: snap(cx), y: snap(ry + bi * blockH), w: snap(colW), h: snap(blockH) });
        });
        colX += colW;
      }
      rowY += rowH;
    }
  }

  return blocks;
}

// ─── Style Panel ──────────────────────────────────────────────────────────────

function StylePanel({ block, onChange, assets, onAssetsChange }) {
  if (!block) {
    return (
      <div className="text-muted text-center py-3" style={{ fontSize: 12 }}>
        Click a block to edit its style
      </div>
    );
  }

  const s = block.style || {};
  const set = (key, val) => onChange({ ...block, style: { ...s, [key]: val } });
  const color = BLOCK_COLORS[block.type] || '#666';

  return (
    <div style={{ fontSize: 12 }}>
      <div className="fw-semibold mb-2 d-flex align-items-center gap-1" style={{ fontSize: 12 }}>
        <span style={{ display: 'inline-block', width: 10, height: 10, background: color, borderRadius: 2, flexShrink: 0 }} />
        {block.type.replace(/_/g, ' ')}
      </div>

      {/* Position & Size */}
      <div className="mb-2">
        <div className="text-muted mb-1" style={{ fontSize: 10 }}>Position &amp; Size (inches)</div>
        <div className="row g-1">
          {[['X', 'x'], ['Y', 'y'], ['W', 'w'], ['H', 'h']].map(([label, key]) => (
            <div className="col-6" key={key}>
              <div className="input-group input-group-sm">
                <span className="input-group-text" style={{ fontSize: 10, padding: '0 5px' }}>{label}</span>
                <input
                  type="number" className="form-control form-control-sm" step={0.05} min={key === 'w' ? 0.1 : key === 'h' ? 0.05 : 0}
                  value={Number(block[key] || 0).toFixed(2)}
                  onChange={e => {
                    const val = parseFloat(e.target.value) || 0;
                    onChange({ ...block, [key]: key === 'w' ? Math.max(0.1, val) : key === 'h' ? Math.max(0.05, val) : Math.max(0, val) });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Font controls */}
      <div className="row g-1 mb-2">
        <div className="col-7">
          <label className="form-label mb-0" style={{ fontSize: 10 }}>Font Size</label>
          <input type="number" className="form-control form-control-sm" min={6} max={36}
            value={s.fontSize || ''} placeholder="auto"
            onChange={e => set('fontSize', e.target.value ? Number(e.target.value) : undefined)} />
        </div>
        <div className="col-5">
          <label className="form-label mb-0" style={{ fontSize: 10 }}>Margin B</label>
          <input type="number" className="form-control form-control-sm" min={0} max={40}
            value={s.marginBottom ?? ''} placeholder="0"
            onChange={e => set('marginBottom', e.target.value !== '' ? Number(e.target.value) : undefined)} />
        </div>
      </div>

      {/* Text style buttons */}
      <div className="d-flex gap-1 mb-2 flex-wrap">
        <button className={`btn btn-sm px-2 ${s.fontWeight === 'bold' ? 'btn-dark' : 'btn-outline-dark'}`}
          onClick={() => set('fontWeight', s.fontWeight === 'bold' ? 'normal' : 'bold')}>
          <strong>B</strong>
        </button>
        <button className={`btn btn-sm px-2 ${s.fontStyle === 'italic' ? 'btn-dark' : 'btn-outline-dark'}`}
          onClick={() => set('fontStyle', s.fontStyle === 'italic' ? 'normal' : 'italic')}>
          <em>I</em>
        </button>
        {['left', 'center', 'right'].map(align => (
          <button key={align}
            className={`btn btn-sm px-2 ${s.textAlign === align ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => set('textAlign', align)}
            title={align}>
            {align === 'left' ? '≡' : align === 'center' ? '≡' : '≡'}
            <span style={{ fontSize: 8, marginLeft: 1 }}>{align[0].toUpperCase()}</span>
          </button>
        ))}
      </div>

      {/* Block-specific options */}
      {block.type === 'nutrition_facts' && (
        <>
          {/* Format selector */}
          <div className="mb-2">
            <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 3 }}>FDA Format</div>
            <div className="btn-group btn-group-sm w-100">
              {[
                { value: 'vertical', label: 'Vertical' },
                { value: 'tabular', label: 'Tabular' },
                { value: 'linear',  label: 'Linear' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  className={`btn ${(block.nfFormat || 'vertical') === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ fontSize: 11 }}
                  onClick={() => onChange({ ...block, nfFormat: value })}
                >
                  {label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 9, color: '#6c757d', marginTop: 2 }}>
              {(block.nfFormat || 'vertical') === 'vertical'  && 'Standard stacked layout — most common packaging'}
              {block.nfFormat === 'tabular'  && 'Horizontal table — when vertical space is limited'}
              {block.nfFormat === 'linear'   && 'Condensed inline — small packs (<12 sq in surface)'}
            </div>
          </div>

          <div className="form-check mb-2">
            <input className="form-check-input" type="checkbox" id="bilingual"
              checked={block.bilingual !== false}
              onChange={e => onChange({ ...block, bilingual: e.target.checked })} />
            <label className="form-check-label" htmlFor="bilingual" style={{ fontSize: 12 }}>Bilingual (EN/ES)</label>
          </div>
          {/* FDA font size compliance note */}
          <div style={{ fontSize: 10, background: '#f8f9fa', border: '1px solid #dee2e6',
            borderRadius: 4, padding: '4px 6px', marginBottom: 6 }}>
            <strong>FDA 21 CFR 101.9(d)</strong><br />
            Min. font size: <strong>8pt</strong> (nutrients) · <strong>13pt</strong> ("Nutrition Facts" heading).
            {(block.style?.fontSize && block.style.fontSize < 8) && (
              <div style={{ color: '#dc3545', marginTop: 3 }}>
                ⚠ Current font size ({block.style.fontSize}) is below the 8pt FDA minimum.
              </div>
            )}
          </div>
        </>
      )}

      {block.type === 'custom_text' && (
        <div className="mb-2">
          <label className="form-label mb-0" style={{ fontSize: 10 }}>Text Content</label>
          <textarea className="form-control form-control-sm" rows={2}
            value={block.customText || ''}
            onChange={e => onChange({ ...block, customText: e.target.value })} />
        </div>
      )}

      {(block.type === 'custom_image' || block.type === 'usda_inspection_mark') && (
        <AssetPicker
          assets={assets || []}
          selectedUrl={block.imageUrl || ''}
          onSelect={(asset) => onChange({ ...block, imageUrl: asset.fileUrl, imageName: asset.name })}
          onAssetsChange={onAssetsChange}
        />
      )}
      {block.type === 'usda_inspection_mark' && (
        <div className="alert alert-danger py-1 px-2 mt-2" style={{ fontSize: 10.5 }}>
          Required by law for USDA/FSIS-regulated products (9 CFR 317/381). Upload your
          company's approved mark-of-inspection artwork — this label cannot be approved
          for a USDA-regulated product without it.
        </div>
      )}
    </div>
  );
}

// ─── Asset Picker — upload & select from library ───────────────────────────

function AssetPicker({ assets, selectedUrl, onSelect, onAssetsChange }) {
  const [uploading, setUploading]   = useState(false);
  const [uploadErr, setUploadErr]   = useState('');
  const [deleting, setDeleting]     = useState(null);
  const fileInputRef                = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ALLOWED = ['image/jpeg','image/jpg','image/png','image/webp','image/gif','image/svg+xml'];
    if (!ALLOWED.includes(file.type)) {
      setUploadErr('Only JPEG, PNG, WebP, GIF, SVG allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadErr('Max file size is 5 MB.');
      return;
    }
    setUploadErr('');
    setUploading(true);
    try {
      const { uploadUrl, publicUrl, key } = await productLabelService.getAssetUploadUrl(file.name, file.type, file.size);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      const asset = await productLabelService.createAsset({ name, fileUrl: publicUrl, s3Key: key, fileSize: file.size, mimeType: file.type });
      onAssetsChange(prev => [asset, ...prev]);
      onSelect(asset);
    } catch {
      setUploadErr('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.name}"?`)) return;
    setDeleting(asset.id);
    try {
      await productLabelService.deleteAsset(asset.id);
      onAssetsChange(prev => prev.filter(a => a.id !== asset.id));
    } catch { /* ignore */ }
    setDeleting(null);
  };

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <span className="text-muted" style={{ fontSize: 10 }}>Image Library</span>
        <button
          className="btn btn-outline-primary btn-sm py-0 px-1"
          style={{ fontSize: 10 }}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : '+ Upload'}
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleFileChange} />
      </div>
      {uploadErr && <div className="text-danger mb-1" style={{ fontSize: 10 }}>{uploadErr}</div>}
      {assets.length === 0 && !uploading && (
        <div className="text-muted text-center py-2" style={{ fontSize: 10 }}>No images yet. Upload one above.</div>
      )}
      <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {assets.map(asset => (
          <div
            key={asset.id}
            onClick={() => onSelect(asset)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
              border: selectedUrl === asset.fileUrl ? '2px solid #be185d' : '1px solid #dee2e6',
              borderRadius: 4, cursor: 'pointer', background: selectedUrl === asset.fileUrl ? '#fdf2f8' : 'white',
            }}
          >
            <img src={asset.fileUrl} alt={asset.name} style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0, borderRadius: 2 }} />
            <span style={{ fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</span>
            <button
              className="btn btn-link btn-sm p-0 text-danger"
              style={{ fontSize: 12, lineHeight: 1 }}
              disabled={deleting === asset.id}
              onClick={e => { e.stopPropagation(); handleDelete(asset); }}
              title="Delete"
            >✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Block Overlay (interactive handle layer on top of the canvas) ─────────────

function BlockOverlay({ block, scale, isSelected, onMouseDownMove, onMouseDownResize, onDelete, onClick }) {
  const PPI = 96;
  const left   = block.x * PPI * scale;
  const top    = block.y * PPI * scale;
  const width  = Math.max(block.w * PPI * scale, 16);
  const height = Math.max(block.h * PPI * scale, 8);
  const color  = BLOCK_COLORS[block.type] || '#666';
  const labelFontSize = Math.max(7, Math.min(11, 9 * scale));

  return (
    <div
      style={{
        position: 'absolute',
        left, top, width, height,
        border: isSelected ? `2px solid ${color}` : `1px dashed ${color}99`,
        backgroundColor: isSelected ? `${color}1a` : `${color}0a`,
        cursor: 'move',
        boxSizing: 'border-box',
        zIndex: isSelected ? 20 : 10,
        userSelect: 'none',
        transition: 'border-color 0.1s',
      }}
      onMouseDown={onMouseDownMove}
      onClick={e => { e.stopPropagation(); onClick(); }}
    >
      {/* Block type badge */}
      <div style={{
        position: 'absolute',
        top: 2, left: 2,
        background: color,
        color: 'white',
        fontSize: labelFontSize,
        padding: '1px 4px',
        borderRadius: 2,
        lineHeight: 1.3,
        maxWidth: 'calc(100% - 20px)',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        opacity: 0.9,
      }}>
        {block.type.replace(/_/g, ' ')}
      </div>

      {/* Delete button */}
      {isSelected && (
        <button
          style={{
            position: 'absolute', top: -9, right: -9,
            width: 18, height: 18, padding: 0,
            background: '#dc3545', color: 'white',
            border: '2px solid white', borderRadius: '50%',
            cursor: 'pointer', fontSize: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, zIndex: 30, boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); onDelete(); }}
          title="Delete block"
        >
          ✕
        </button>
      )}

      {/* Resize handle (bottom-right corner) */}
      {isSelected && width > 24 && height > 14 && (
        <div
          style={{
            position: 'absolute', bottom: -5, right: -5,
            width: 14, height: 14,
            background: color,
            border: '2px solid white',
            cursor: 'se-resize',
            borderRadius: 3,
            zIndex: 30,
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
          onMouseDown={e => { e.stopPropagation(); onMouseDownResize(e); }}
          title="Drag to resize"
        />
      )}

      {/* Right-edge resize handle */}
      {isSelected && height > 20 && (
        <div
          style={{
            position: 'absolute', top: '50%', right: -4,
            width: 8, height: 20,
            background: color,
            border: '2px solid white',
            cursor: 'e-resize',
            borderRadius: 3,
            transform: 'translateY(-50%)',
            zIndex: 30,
          }}
          onMouseDown={e => { e.stopPropagation(); onMouseDownResize(e, 'e'); }}
          title="Drag to resize width"
        />
      )}

      {/* Bottom-edge resize handle */}
      {isSelected && width > 40 && (
        <div
          style={{
            position: 'absolute', bottom: -4, left: '50%',
            width: 20, height: 8,
            background: color,
            border: '2px solid white',
            cursor: 's-resize',
            borderRadius: 3,
            transform: 'translateX(-50%)',
            zIndex: 30,
          }}
          onMouseDown={e => { e.stopPropagation(); onMouseDownResize(e, 's'); }}
          title="Drag to resize height"
        />
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function LabelDesignPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  // ── Design state ────────────────────────────────────────────────────────────
  const [designName, setDesignName]     = useState('Untitled Design');
  const [preset, setPreset]             = useState('4×6 Landscape (default)');
  const [customWidth, setCustomWidth]   = useState(6);
  const [customHeight, setCustomHeight] = useState(4);
  const [canvasBlocks, setCanvasBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState('');
  const [loading, setLoading]           = useState(isEditing);

  // ── Clone state ──────────────────────────────────────────────────────────────
  const [allDesigns, setAllDesigns]     = useState([]);
  const [cloneSourceId, setCloneSourceId] = useState('');

  // ── Label asset library ───────────────────────────────────────────────────────
  const [assets, setAssets] = useState([]);

  // ── Preview width tracking ───────────────────────────────────────────────────
  const previewContainerRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(700);

  // ── Drag state (refs to avoid stale closures) ────────────────────────────────
  const dragRef       = useRef(null); // { blockId, mode, startX, startY, origX, origY, origW, origH }
  const scaleRef      = useRef(1);
  const labelDimsRef  = useRef({ width: 6, height: 4 });

  // ── Computed ─────────────────────────────────────────────────────────────────
  const getSize = () => {
    if (preset === 'Custom') return { width: parseFloat(customWidth) || 6, height: parseFloat(customHeight) || 4 };
    const found = LABEL_PRESETS.find(p => p.label === preset);
    return found?.width ? { width: found.width, height: found.height } : { width: 6, height: 4 };
  };
  const { width: labelWidth, height: labelHeight } = getSize();

  const scale = Math.min(1.4, Math.max(0.25, (previewWidth - 48) / (labelWidth * 96)));

  const canvasDesign = useMemo(
    () => ({ width: labelWidth, height: labelHeight, canvas: canvasBlocks }),
    [labelWidth, labelHeight, canvasBlocks]
  );

  const selectedBlock = canvasBlocks.find(b => b.id === selectedBlockId) || null;

  // Keep refs updated so drag handlers always have fresh values
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { labelDimsRef.current = { width: labelWidth, height: labelHeight }; }, [labelWidth, labelHeight]);

  // ── Global drag listeners ────────────────────────────────────────────────────
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { blockId, mode, startX, startY, origX, origY, origW, origH } = dragRef.current;
      const PPI = 96;
      const s = scaleRef.current;
      const { width: lW, height: lH } = labelDimsRef.current;
      const dx = (e.clientX - startX) / (PPI * s);
      const dy = (e.clientY - startY) / (PPI * s);

      setCanvasBlocks(prev => prev.map(b => {
        if (b.id !== blockId) return b;
        if (mode === 'move') {
          return {
            ...b,
            x: snap(clamp(origX + dx, 0, lW - b.w)),
            y: snap(clamp(origY + dy, 0, lH - b.h)),
          };
        }
        if (mode === 'resize' || mode === 'resize-se') {
          return {
            ...b,
            w: snap(clamp(origW + dx, 0.2, lW - b.x)),
            h: snap(clamp(origH + dy, 0.05, lH - b.y)),
          };
        }
        if (mode === 'resize-e') {
          return { ...b, w: snap(clamp(origW + dx, 0.2, lW - b.x)) };
        }
        if (mode === 'resize-s') {
          return { ...b, h: snap(clamp(origH + dy, 0.05, lH - b.y)) };
        }
        return b;
      }));
    };

    const onUp = () => { dragRef.current = null; };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []); // empty deps — reads only from refs

  // ── Observe preview container width ─────────────────────────────────────────
  useEffect(() => {
    const el = previewContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setPreviewWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setPreviewWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // ── Load all designs for clone list ─────────────────────────────────────────
  useEffect(() => {
    productLabelService.getDesigns().then(setAllDesigns).catch(() => {});
    productLabelService.getAssets().then(setAssets).catch(() => {});
  }, []);

  // ── Load existing design ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditing) return;
    productLabelService.getDesigns()
      .then(designs => {
        const found = designs.find(d => String(d.id) === String(id));
        if (!found) return;
        setDesignName(found.name || 'Untitled Design');

        const w = parseFloat(found.width) || 6;
        const h = parseFloat(found.height) || 4;
        const matchPreset = LABEL_PRESETS.find(p => p.width === w && p.height === h);
        if (matchPreset) {
          setPreset(matchPreset.label);
        } else {
          setPreset('Custom');
          setCustomWidth(w);
          setCustomHeight(h);
        }

        const layout = found.rows || {};
        if (Array.isArray(layout.canvas)) {
          setCanvasBlocks(layout.canvas);
        } else if (layout.columns || layout.rows) {
          setCanvasBlocks(convertToCanvas(layout, w, h));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  // ── Drag start ───────────────────────────────────────────────────────────────
  const startDrag = useCallback((e, block, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedBlockId(block.id);
    dragRef.current = {
      blockId: block.id, mode,
      startX: e.clientX, startY: e.clientY,
      origX: block.x, origY: block.y, origW: block.w, origH: block.h,
    };
  }, []);

  // ── Block operations ─────────────────────────────────────────────────────────
  const addBlock = useCallback((type) => {
    const d = BLOCK_DEFAULTS[type] || { w: 2, h: 0.5 };
    const { width: lW, height: lH } = labelDimsRef.current;
    // Place at center, clamped so it fits within label bounds
    const x = snap(clamp((lW - d.w) / 2, 0, Math.max(0, lW - d.w)));
    const y = snap(clamp((lH - d.h) / 2, 0, Math.max(0, lH - d.h)));
    const newBlock = { id: uid(), type, x, y, w: d.w, h: d.h, style: {} };
    setCanvasBlocks(prev => [...prev, newBlock]);
    setSelectedBlockId(newBlock.id);
  }, []);

  const deleteBlock = useCallback((blockId) => {
    setCanvasBlocks(prev => prev.filter(b => b.id !== blockId));
    setSelectedBlockId(cur => cur === blockId ? null : cur);
  }, []);

  const updateBlock = useCallback((updated) => {
    setCanvasBlocks(prev => prev.map(b => b.id === updated.id ? updated : b));
  }, []);

  // Move in z-order (layers): dir +1 = toward top, -1 = toward bottom
  const moveLayer = useCallback((blockId, dir) => {
    setCanvasBlocks(prev => {
      const idx = prev.findIndex(b => b.id === blockId);
      const t = idx + dir;
      if (idx < 0 || t < 0 || t >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[t]] = [arr[t], arr[idx]];
      return arr;
    });
  }, []);

  // ── Clone layout from another design ────────────────────────────────────────
  const handleClone = () => {
    const src = allDesigns.find(d => String(d.id) === String(cloneSourceId));
    if (!src) return;
    const w = parseFloat(src.width) || 6;
    const h = parseFloat(src.height) || 4;
    const matchPreset = LABEL_PRESETS.find(p => p.width === w && p.height === h);
    if (matchPreset) {
      setPreset(matchPreset.label);
    } else {
      setPreset('Custom');
      setCustomWidth(w);
      setCustomHeight(h);
    }
    const layout = src.rows || {};
    if (Array.isArray(layout.canvas)) {
      setCanvasBlocks(layout.canvas.map(b => ({ ...b, id: uid() })));
    } else if (layout.columns || layout.rows) {
      setCanvasBlocks(convertToCanvas(layout, w, h));
    }
    setCloneSourceId('');
    setSelectedBlockId(null);
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        name: designName,
        width: labelWidth,
        height: labelHeight,
        rows: { canvas: canvasBlocks },
      };
      if (isEditing) {
        await productLabelService.updateDesign(id, payload);
      } else {
        await productLabelService.createDesign(payload);
      }
      navigate('/label-designs');
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) return <div className="container mt-5 text-muted">Loading design…</div>;

  const canvasPxW = labelWidth  * 96 * scale;
  const canvasPxH = labelHeight * 96 * scale;

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', overflow: 'hidden' }}>

      {/* ── Top bar ── */}
      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-white flex-shrink-0" style={{ minHeight: 52 }}>
        <Link to="/label-designs" className="btn btn-outline-secondary btn-sm">← Back</Link>
        <Link to="/labels" className="btn btn-link btn-sm text-muted px-1" style={{ fontSize: 12 }}>Labels</Link>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 260 }}
          value={designName}
          onChange={e => setDesignName(e.target.value)}
          placeholder="Design name"
        />
        <span className="text-muted d-none d-md-inline" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {labelWidth}" × {labelHeight}" · {Math.round(scale * 100)}%
        </span>
        <div className="ms-auto d-flex gap-2 align-items-center">
          {saveError && <span className="text-danger" style={{ fontSize: 12 }}>{saveError}</span>}
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Design'}
          </button>
        </div>
      </div>

      <div className="d-flex flex-grow-1" style={{ overflow: 'hidden' }}>

        {/* ── Left Sidebar ── */}
        <div
          className="border-end bg-light flex-shrink-0"
          style={{ width: 250, overflowY: 'auto', padding: '10px 8px', fontSize: 12 }}
        >
          {/* Label Size */}
          <div className="mb-3">
            <div className="fw-semibold mb-1" style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>Label Size</div>
            <select className="form-select form-select-sm mb-1" value={preset} onChange={e => setPreset(e.target.value)}>
              {LABEL_PRESETS.map(p => <option key={p.label} value={p.label}>{p.label}</option>)}
            </select>
            {preset === 'Custom' && (
              <div className="row g-1">
                <div className="col-6">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text" style={{ fontSize: 10 }}>W"</span>
                    <input type="number" className="form-control" value={customWidth}
                      onChange={e => setCustomWidth(e.target.value)} step={0.5} min={1} />
                  </div>
                </div>
                <div className="col-6">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text" style={{ fontSize: 10 }}>H"</span>
                    <input type="number" className="form-control" value={customHeight}
                      onChange={e => setCustomHeight(e.target.value)} step={0.5} min={1} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Clone from another design */}
          {allDesigns.filter(d => String(d.id) !== String(id)).length > 0 && (
            <div className="mb-3">
              <div className="fw-semibold mb-1" style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>Clone Layout From</div>
              <div className="d-flex gap-1">
                <select className="form-select form-select-sm flex-grow-1" value={cloneSourceId} onChange={e => setCloneSourceId(e.target.value)}>
                  <option value="">Select design…</option>
                  {allDesigns.filter(d => String(d.id) !== String(id)).map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleClone} disabled={!cloneSourceId}>
                  Clone
                </button>
              </div>
            </div>
          )}

          {/* Block palette */}
          <div className="mb-3">
            <div className="fw-semibold mb-1" style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>Add Block</div>
            <div className="d-flex flex-column gap-1">
              {BLOCK_TYPES.map(bt => {
                const color = BLOCK_COLORS[bt.value] || '#888';
                return (
                  <button
                    key={bt.value}
                    className="btn btn-sm text-start d-flex align-items-center gap-2"
                    style={{
                      fontSize: 11, padding: '4px 7px',
                      background: 'white',
                      border: `1px solid ${color}55`,
                      color: '#1f2937',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = `${color}15`}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    onClick={() => addBlock(bt.value)}
                  >
                    <span style={{
                      display: 'inline-block', width: 10, height: 10,
                      background: color, borderRadius: 2, flexShrink: 0,
                    }} />
                    {bt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Layers panel */}
          {canvasBlocks.length > 0 && (
            <div className="mb-3">
              <div className="fw-semibold mb-1" style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>
                Layers <span className="fw-normal text-muted">(↑ = front)</span>
              </div>
              {/* Reversed so highest-index (topmost) block is at top of list */}
              {[...canvasBlocks].reverse().map((b, revIdx) => {
                const idx = canvasBlocks.length - 1 - revIdx;
                const color = BLOCK_COLORS[b.type] || '#888';
                const isSelected = selectedBlockId === b.id;
                return (
                  <div
                    key={b.id}
                    className="d-flex align-items-center gap-1 mb-1 rounded px-1"
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? `${color}22` : 'white',
                      border: isSelected ? `1px solid ${color}` : '1px solid #e5e7eb',
                      padding: '3px 5px',
                    }}
                    onClick={() => setSelectedBlockId(isSelected ? null : b.id)}
                  >
                    <span style={{ display: 'inline-block', width: 8, height: 8, background: color, borderRadius: 1, flexShrink: 0 }} />
                    <span className="flex-grow-1 text-truncate" style={{ fontSize: 11 }}>
                      {b.type.replace(/_/g, ' ')}
                    </span>
                    <div className="d-flex" style={{ gap: 0 }}>
                      <button
                        className="btn btn-link p-0 text-muted"
                        style={{ fontSize: 11, lineHeight: 1, minWidth: 14 }}
                        title="Move forward"
                        onClick={e => { e.stopPropagation(); moveLayer(b.id, 1); }}
                        disabled={idx === canvasBlocks.length - 1}
                      >↑</button>
                      <button
                        className="btn btn-link p-0 text-muted"
                        style={{ fontSize: 11, lineHeight: 1, minWidth: 14 }}
                        title="Move backward"
                        onClick={e => { e.stopPropagation(); moveLayer(b.id, -1); }}
                        disabled={idx === 0}
                      >↓</button>
                      <button
                        className="btn btn-link p-0 text-danger"
                        style={{ fontSize: 11, lineHeight: 1, minWidth: 14 }}
                        title="Delete"
                        onClick={e => { e.stopPropagation(); deleteBlock(b.id); }}
                      >✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Style panel */}
          <div>
            <div className="fw-semibold mb-1" style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.05em' }}>Style</div>
            <div className="border rounded p-2 bg-white">
              <StylePanel block={selectedBlock} onChange={updateBlock} assets={assets} onAssetsChange={setAssets} />
            </div>
          </div>
        </div>

        {/* ── Canvas Area ── */}
        <div
          ref={previewContainerRef}
          className="flex-grow-1 d-flex align-items-center justify-content-center"
          style={{ background: '#d1d5db', overflow: 'auto', padding: 32 }}
        >
          <div>
            {/* Hint */}
            <div className="text-center mb-2" style={{ fontSize: 11, color: '#6b7280' }}>
              Click to select · Drag to move · Corner/edge handles to resize · Click outside to deselect
            </div>

            {/* Label canvas wrapper */}
            <div
              style={{
                position: 'relative',
                width: canvasPxW,
                height: canvasPxH,
                boxShadow: '0 4px 32px rgba(0,0,0,0.22)',
                cursor: 'default',
              }}
              onClick={() => setSelectedBlockId(null)}
            >
              {/* Actual label content (LabelRenderer handles canvas mode) */}
              <LabelRenderer design={canvasDesign} scale={scale} isDesign={true} />

              {/* Interactive overlay — one BlockOverlay per block */}
              <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
                {canvasBlocks.map(block => (
                  <BlockOverlay
                    key={block.id}
                    block={block}
                    scale={scale}
                    isSelected={selectedBlockId === block.id}
                    onMouseDownMove={e => startDrag(e, block, 'move')}
                    onMouseDownResize={(e, direction) => startDrag(e, block, direction === 'e' ? 'resize-e' : direction === 's' ? 'resize-s' : 'resize-se')}
                    onDelete={() => deleteBlock(block.id)}
                    onClick={() => setSelectedBlockId(block.id)}
                  />
                ))}

                {canvasBlocks.length === 0 && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#9ca3af', fontSize: 14, pointerEvents: 'none', gap: 8,
                  }}>
                    <div style={{ fontSize: 32 }}>🏷️</div>
                    <div>Add blocks from the sidebar to start designing</div>
                  </div>
                )}
              </div>
            </div>

            {/* Ruler hint */}
            <div className="text-center mt-2" style={{ fontSize: 10, color: '#9ca3af' }}>
              {labelWidth}" × {labelHeight}" — snaps to 0.05"
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LabelDesignPage;
